/**
 * Footer columns: accordions on mobile, plain lists on desktop.
 *
 * The markup ships as <details open> so that with JavaScript off every link is
 * reachable — the honest degradation. This collapses them on mobile and
 * re-opens them above the breakpoint.
 *
 * This has to be JS. A closed <details> hides its children through the UA
 * shadow tree, and no author-level display rule overrides that, so a CSS-only
 * version silently drops every footer link at desktop widths.
 */
(() => {
  const BREAKPOINT = '(min-width: 750px)';
  const cols = document.querySelectorAll('.cfootref__col');
  if (!cols.length) return;

  const mq = window.matchMedia(BREAKPOINT);

  /* THE name ATTRIBUTE HAS TO COME OFF ON DESKTOP.
     <details name="..."> is an exclusive group at EVERY width — the browser
     permits exactly one open member. The inline script in the section applies
     it on mobile, where one-at-a-time is wanted, and it used to stay applied
     for the rest of the session. So on a viewport that started narrow and then
     widened, this function set all four open and the browser immediately shut
     three of them, keeping only the LAST — which is why Shop, Shop by and Help
     showed a heading with nothing under it while Calmy still had its links.

     Removing the attribute BEFORE setting open is load-bearing: with it still
     present, opening the second column closes the first. */
  const sync = () => cols.forEach((col) => {
    if (mq.matches) {
      col.removeAttribute("name");
      col.open = true;
    } else {
      col.setAttribute("name", "calmy-footer-nav");
      col.open = false;
    }
  });

  sync();
  mq.addEventListener('change', sync);
})();

/**
 * Fits the wordmark sign-off to the content width, optically.
 *
 * The distinction matters. A text run's LAYOUT box includes each end glyph's
 * side bearing — the built-in breathing room either side of the ink. Size the
 * layout box to the container and the visible letters land a few pixels inside
 * it, so the C and the y sit fractionally in from the columns above and the
 * whole line reads as slightly misaligned without it being obvious why.
 *
 * So: measure the INK, scale until the ink spans the container, then pull the
 * bearings back off with negative margins. What lines up is what you can see.
 *
 * Canvas is used for the measurement because measureText exposes
 * actualBoundingBoxLeft/Right — the true ink extents. A DOM Range gives the
 * layout box only, which is the thing we are trying to correct for, so it is
 * kept solely as the fallback when the ink metrics are unavailable.
 *
 * There is no maximum size. This is the last element on the page with nothing
 * after it, so it cannot crowd anything below it.
 */
(function () {
  "use strict";

  var mark = document.querySelector(".cfootref__mark");
  if (!mark) return;

  var REF = 100; // measure once at a reference size, then scale linearly
  var ctx = null;
  try { ctx = document.createElement("canvas").getContext("2d"); } catch (e) { ctx = null; }

  function avail() {
    var cs = getComputedStyle(mark);
    return mark.getBoundingClientRect().width
      - parseFloat(cs.paddingLeft || 0)
      - parseFloat(cs.paddingRight || 0);
  }

  /* Width of the laid-out text run, straight from the engine. This is the one
     number that is always true: it already accounts for the real font, kerning
     and CSS letter-spacing, whatever the browser is. */
  function runWidth() {
    var r = document.createRange();
    r.selectNodeContents(mark);
    return r.getBoundingClientRect().width;
  }

  /* Side bearings as a FRACTION of font size. Canvas is the only source for
     these, and it is used for nothing else — so if it is missing or wrong, the
     mark is merely a few pixels inset rather than the wrong size. */
  function bearingRatios(cs, word) {
    if (!ctx) return { l: 0, r: 0 };
    try {
      ctx.font = [cs.fontStyle, cs.fontWeight, REF + "px", cs.fontFamily].join(" ");
      /* measureText ignores CSS letter-spacing and this face is tracked at
         -0.04em. Set AFTER font, since assigning `font` resets spacing in some
         engines. Where ctx.letterSpacing does not exist at all — older Safari
         and Firefox — the ratios come out slightly wide, which costs a few
         pixels of inset and never a clipped glyph. */
      if ("letterSpacing" in ctx) {
        var lsRatio = (parseFloat(cs.letterSpacing) || 0) / (parseFloat(cs.fontSize) || 1);
        ctx.letterSpacing = (lsRatio * REF).toFixed(3) + "px";
      }
      var m = ctx.measureText(word);
      if (!(m.width > 0)) return { l: 0, r: 0 };
      return {
        l: -m.actualBoundingBoxLeft / REF,
        r: (m.width - m.actualBoundingBoxRight) / REF
      };
    } catch (e) {
      return { l: 0, r: 0 };
    }
  }

  function fit() {
    // Reset before measuring: never compound the previous fit.
    mark.style.fontSize = "";
    mark.style.marginLeft = "";
    mark.style.marginRight = "";

    var width = avail();
    if (!(width > 0)) return;

    var word = (mark.textContent || "").trim();
    if (!word) return;

    var cs = getComputedStyle(mark);
    var b = bearingRatios(cs, word);

    /* Solve against the ENGINE's own measurement rather than canvas's.
         run(S) = k · S            (linear — the tracking is em-based)
         ink(S) = (k − bl − br) · S
       want ink === width, so S = width / (k − bl − br).

       Anchoring to run() is what makes this safe cross-browser: canvas can be
       wrong about the advance, and when it was, the mark came out the wrong
       SIZE. Here a bad canvas reading can only shift the trim. */
    var s0 = parseFloat(cs.fontSize) || 100;
    var k = runWidth() / s0;
    var denom = k - b.l - b.r;
    if (!(denom > 0)) return;

    var size = width / denom;
    mark.style.fontSize = size.toFixed(1) + "px";

    /* Verify and correct once against the real layout. If the engine disagrees
       with the linear model — a fallback face swapping in, a rounding step —
       this pulls it back rather than shipping the discrepancy. */
    for (var i = 0; i < 2; i++) {
      var ink = runWidth() - (b.l + b.r) * size;
      if (!(ink > 0)) break;
      var err = width - ink;
      if (Math.abs(err) <= 0.5) break;
      size *= width / ink;
      mark.style.fontSize = size.toFixed(1) + "px";
    }

    /* Never wider than the box it sits in. If anything above still left the run
       overhanging, take the size down until it cannot clip — a slightly small
       wordmark is a design choice, a clipped one is a bug. */
    var guard = 0;
    while (runWidth() - (b.l + b.r) * size > width + 0.5 && guard++ < 4) {
      size *= width / (runWidth() - (b.l + b.r) * size);
      mark.style.fontSize = size.toFixed(1) + "px";
    }

    /* Trim the bearings so the visible ink, not the layout box, meets the
       gutters. Clamped: a negative margin deeper than the page gutter would
       push ink past the viewport edge and give the document a scrollbar. */
    var gutter = (parseFloat(getComputedStyle(mark.parentElement).paddingLeft) || 20) * 2;
    var lt = Math.min(Math.max(b.l * size, 0), gutter);
    var rt = Math.min(Math.max(b.r * size, 0), gutter);
    if (lt) mark.style.marginLeft = (-lt).toFixed(1) + "px";
    if (rt) mark.style.marginRight = (-rt).toFixed(1) + "px";
  }

  fit();

  /* Webfonts change every metric this depends on, and `fonts.ready` resolves
     exactly once — if the display face arrives after that (a late or lazy
     load), the size stays the one computed for the fallback. Playfair is wider
     per em than the Georgia fallback, so that combination sizes the mark too
     large and the final glyph runs past the gutter and gets clipped.
     `loadingdone` fires for every subsequent load, so the swap is caught. */
  if (document.fonts) {
    if (document.fonts.ready) document.fonts.ready.then(fit);
    if (document.fonts.addEventListener) document.fonts.addEventListener("loadingdone", fit);
  }

  /* Any reflow that changes the available width — window resize, a scrollbar
     appearing, the theme editor resizing the frame. More reliable than resize
     alone, which does not fire for container-only changes.

     WIDTH ONLY, and that is not a detail: fit() changes the mark's font size,
     which changes the parent's HEIGHT, which would re-enter the observer and
     loop forever. Re-fitting only when the width actually moves breaks the
     cycle, because fit() cannot change its own container's width. */
  if (window.ResizeObserver && mark.parentElement) {
    var lastWidth = mark.parentElement.getBoundingClientRect().width;
    new ResizeObserver(function () {
      var w = mark.parentElement.getBoundingClientRect().width;
      if (Math.abs(w - lastWidth) < 0.5) return;
      lastWidth = w;
      fit();
    }).observe(mark.parentElement);
  } else {
    window.addEventListener("resize", fit, { passive: true });
  }
})();

/* Exclusive accordion fallback.

   <details name=""> gives this natively in Chrome 120+, Safari 17.2+ and
   Firefox 130+. This closes the gap for anything older: open one, the others
   close. Feature-detected, so where the browser already does it this listener
   never runs and cannot fight it. */
(function () {
  "use strict";
  var supportsName = (function () {
    var d = document.createElement("details");
    return "name" in d;
  })();
  if (supportsName) return;

  var cols = [].slice.call(document.querySelectorAll('details.cfootref__col'));
  cols.forEach(function (d) {
    d.addEventListener("toggle", function () {
      if (!d.open) return;
      cols.forEach(function (o) { if (o !== d) o.open = false; });
    });
  });
})();
